import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

interface MultiSelectProps {
  id?: string;
  options: string[];
  selected: string[];
  setSelected: (values: string[]) => void;
  disabledOptions?: string[];
  selectionMode?: "multiple" | "single";
  showSearch?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  codexId?: string;
  disablePortal?: boolean;
  avoidCollisions?: boolean;
  modal?: boolean;
}

export function MultiSelect({
  id,
  options,
  selected,
  setSelected,
  disabledOptions = [],
  selectionMode = "multiple",
  showSearch = true,
  disabled = false,
  placeholder = "Vyberte položky",
  searchPlaceholder = "Hledat...",
  className = "w-full col-span-4",
  codexId,
  disablePortal = false,
  avoidCollisions = false,
  modal = true,
}: MultiSelectProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      placeholder: "Vyberte položky",
      searchPlaceholder: "Hledat...",
      empty: "Žádné výsledky.",
    },
    en: {
      placeholder: "Select items",
      searchPlaceholder: "Search...",
      empty: "No results.",
    },
  }[language];
  const resolvedPlaceholder =
    placeholder === "Vyberte položky" ? copy.placeholder : placeholder;
  const resolvedSearchPlaceholder =
    searchPlaceholder === "Hledat..."
      ? copy.searchPlaceholder
      : searchPlaceholder;
  const [open, setOpen] = useState(false);

  const toggleItem = (item: string) => {
    const safeSelected = Array.isArray(selected) ? selected : [];

    if (disabledOptions.includes(item)) {
      return;
    }

    if (selectionMode === "single") {
      setSelected(safeSelected.includes(item) ? [] : [item]);
      setOpen(false);
      return;
    }

    setSelected(
      safeSelected.includes(item)
        ? safeSelected.filter((i) => i !== item)
        : [...safeSelected, item],
    );
  };

  const safeSelected = Array.isArray(selected) ? selected : [];

  return (
    <Popover modal={modal} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          codex-id={codexId}
          type="button"
          variant="unstyled"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            "app-input-base mb-2 flex items-center justify-between whitespace-nowrap text-left [&>span]:line-clamp-1",
            disabled && "cursor-not-allowed opacity-50",
            safeSelected.length > 0 ? "text-foreground" : "text-foreground",
            className,
          )}
        >
          <span>
            {safeSelected.length > 0
              ? safeSelected.join(", ")
              : resolvedPlaceholder}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={avoidCollisions}
        disablePortal={disablePortal}
        className="max-h-[min(24rem,var(--radix-popover-content-available-height))] min-w-[var(--radix-popover-trigger-width)] w-max max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border/70 bg-background p-0 text-foreground shadow-xl"
      >
        <Command className="rounded-lg bg-background text-foreground">
          {showSearch && (
            <CommandInput
              placeholder={resolvedSearchPlaceholder}
              className="h-9 text-sm"
            />
          )}
          <CommandList className="max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain p-1">
            <CommandEmpty className="py-3 text-sm text-foreground">
              {copy.empty}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  disabled={disabledOptions.includes(option)}
                  onSelect={() => toggleItem(option)}
                  className="w-full whitespace-nowrap rounded-lg py-2 pl-3 pr-3 text-foreground data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50 data-[selected=true]:bg-hover data-[selected=true]:text-foreground"
                >
                  <span className="min-w-0 pr-4">{option}</span>
                  <Check
                    className={cn(
                      "ml-auto size-4 shrink-0 text-foreground",
                      safeSelected.includes(option)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
